import { ComponentPropsWithoutRef, useMemo, useState, useEffect } from 'react';

import { Box, Skeleton } from '@mui/material';
import {
    Chart as ChartJS,
    ChartOptions,
    ChartType,
    LayoutPosition,
    registerables,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useLocation } from 'react-router-dom';

import { usePageStorage } from 'hooks/api/builder/usePageStorage';
import { useParseChart } from 'hooks/utils/parseChart';

import { ChartWrapper } from './InteractiveChart.styled';

ChartJS.register(...registerables);

export type ChartDsColor = {
    label: string;
    backgroundColor?: string;
    borderColor?: string;
};

export type ChartConfig = {
    indexAxis?: ChartOptions['indexAxis'];
    legendPos?: LayoutPosition;
    stack?: boolean;
    height?: number;
    type: ChartType;
    group?: boolean;
    parsingOption?: 'index' | 'rowMapping';
    file?: string;
    chartColors?: ChartDsColor[];
    gridX?: boolean;
    gridY?: boolean;
};

export type InteractiveChartProps = ComponentPropsWithoutRef<'div'> & {
    config?: ChartConfig;
    children?: never;
};

const LegendSkeleton = (
    <>
        <Skeleton
            variant="text"
            width="60%"
            sx={{ fontSize: '1rem', margin: '0 auto' }}
        />
        <Skeleton
            variant="text"
            width="40%"
            sx={{
                fontSize: '0.7rem',
                margin: '0 auto 5px',
            }}
        />
    </>
);

export const InteractiveChart = ({
    config,
    ...rest
}: InteractiveChartProps): JSX.Element => {
    const {
        type = 'bar',
        height = 1,
        indexAxis = 'x',
        legendPos = 'top',
        stack = false,
        gridX = true,
        gridY = true,
    } = config || {};

    const location = useLocation();
    const [isAdmin, setIsAdmin] = useState<boolean>();

    useEffect(() => {
        if (location.pathname.includes('content_manager')) {
            setIsAdmin(true);
        } else {
            setIsAdmin(false);
        }
    }, [location.pathname]);

    const options: ChartOptions = {
        indexAxis,
        plugins: {
            legend: {
                position: legendPos,
            },
            tooltip: {
                callbacks: {
                    label(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += Number.isNaN(context.parsed.x)
                                ? '-'
                                : context.parsed.x;
                        }

                        return label;
                    },
                },
            },
        },
        interaction: {
            intersect: false,
            mode: indexAxis,
        },
        scales: {
            x: {
                grid: {
                    display: gridX,
                },
                stacked: stack,
            },
            y: {
                grid: {
                    display: gridY,
                },
                stacked: stack,
            },
        },
        maintainAspectRatio: false,
    };

    const { file, ...conf } = config || {};

    const { findFile } = usePageStorage();

    const selectedFile = useMemo(() => {
        if (file) {
            return findFile(file)?.file;
        }
        return undefined;
    }, [file, findFile]);

    const { data, loading } = useParseChart({
        ...conf,
        type,
        file: selectedFile,
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                minWidth: '100%',
            }}
        >
            <ChartWrapper {...rest}>
                {isAdmin && data.datasets.length === 0 ? (
                    <div
                        style={{
                            width: '100%',
                            textAlign: 'center',
                            margin: '30px 0',
                        }}
                    >
                        Загрузите источник данных
                    </div>
                ) : undefined}
                {!loading ? (
                    <Chart
                        key={JSON.stringify({
                            ...config,
                            ...data.labels,
                        })}
                        type={type}
                        className="chart_canvas"
                        options={options}
                        height={height * 100}
                        data={data}
                        redraw
                    />
                ) : (
                    <>
                        {legendPos === 'top' ? LegendSkeleton : undefined}

                        <Skeleton
                            variant="rectangular"
                            animation="wave"
                            height={height * 100}
                        />

                        {legendPos === 'bottom' ? LegendSkeleton : undefined}
                    </>
                )}
            </ChartWrapper>
        </Box>
    );
};
